/**
 * Centralized section descriptions for community management
 * These descriptions explain what each section does and help users understand the features
 */

export const SECTION_DESCRIPTIONS = {
  TOPICS: {
    title: 'About Topics',
    content: `Topics are content categories that help you organize and manage different types of user-generated content in your community. They act as organizational containers that allow you to apply specific rules and processes to different kinds of content.

**Key benefits:**
• Apply specific moderation rules to different content types
• Organize content for better discoverability
• Streamline moderation workflows with targeted approaches

Use topics to separate player communications, user uploads, and community discussions, ensuring the right moderation approach is applied to each content type.`,
  },

  ACTIONS: {
    title: 'About Actions',
    content: `Actions define what happens when content violates your community guidelines. They're the automated responses that help maintain community standards without requiring constant manual intervention.

**Key benefits:**
• Automate moderation responses for consistent enforcement
• Scale your moderation efforts efficiently
• Provide clear consequences for policy violations

Common actions include warnings, content removal, temporary restrictions, and permanent bans. Each action can be configured with specific triggers and escalation paths.`,
  },

  WORKFLOWS: {
    title: 'About Workflows',
    content: `Workflows connect your topics and actions into complete moderation processes. They define the step-by-step journey content takes from submission to final disposition.

**Key benefits:**
• Create comprehensive moderation pipelines
• Ensure consistent handling across all content types
• Balance automation with human oversight

Workflows can include multiple stages like automated screening, human review, appeals processes, and final actions, giving you complete control over your community's moderation experience.`,
  },

  CLASSIFICATION_WORKFLOWS: {
    title: 'About Classification Workflows',
    content: `Classification workflows automatically categorize and evaluate content based on your community guidelines. They combine **topics** and **actions** to create complete moderation processes.

**How Topics & Actions Work Together:**
• **Topics** define what content to classify (e.g., chat messages, user uploads, forum posts)
• **Actions** determine what happens when classification detects policy violations
• **Workflows** connect topics to actions, creating automated moderation pipelines

**Key benefits:**
• Automatically detect policy violations and harmful content
• Reduce manual moderation workload through intelligent pre-screening
• Ensure consistent content evaluation across your community
• Configure API settings for optimal performance and throughput

Classification workflows are essential for maintaining community safety at scale, providing the first line of defense against inappropriate content.`,
  },

  REPORTING_WORKFLOWS: {
    title: 'About Reporting Workflows',
    content: `Reporting workflows handle user-generated reports and complaints about content or behavior in your community. They combine **topics** and **actions** to create systematic report processing.

**How Topics & Actions Work Together:**
• **Topics** define what types of content can be reported (e.g., harassment, spam, inappropriate content)
• **Actions** determine what happens when reports are validated (e.g., content removal, user warnings, escalation)
• **Workflows** connect reported topics to appropriate actions, ensuring consistent report handling

**Key benefits:**
• Systematically process user reports and complaints
• Track report status and resolution outcomes
• Generate insights and analytics on community issues
• Configure API settings for report processing efficiency

Reporting workflows ensure that community concerns are addressed promptly and fairly, maintaining trust and safety within your community.`,
  },

  MODERATION_WORKFLOWS: {
    title: 'About Moderation Workflows',
    content: `Moderation workflows provide comprehensive human-led moderation processes for reviewing and addressing content that requires moderator attention. They combine **topics** and **actions** to create systematic moderation pipelines.

**How Topics & Actions Work Together:**
• **Topics** define what content requires moderation review (e.g., flagged content, appeals, borderline cases)
• **Actions** determine what happens after moderator review (e.g., approve, remove, escalate, ban)
• **Workflows** connect moderation topics to appropriate actions, ensuring consistent moderator decisions

**Key benefits:**
• Enable human oversight for complex or sensitive content decisions
• Provide moderators with structured review processes
• Ensure consistent enforcement of community guidelines
• Track moderator actions and decision patterns

Moderation workflows balance automation with human judgment, ensuring that your community maintains high standards while addressing nuanced situations that require expert review.`,
  },

  COMMUNITY_GROUPS: {
    title: 'About Community Groups',
    content: `Community Groups allow you to organize users into distinct segments with their own rules, permissions, and moderation approaches. This enables more nuanced community management.

**Key benefits:**
• Segment users based on behavior, role, or other criteria
• Apply different moderation standards to different groups
• Create specialized experiences for different user types

Groups can have their own topics, actions, and workflows, allowing you to tailor the community experience to different user needs and trust levels.`,
  },
} as const;
